<template>
  <div>
    <transition-group name="fade">
      <div v-if="isShowingBikkuraponAnimationModal" :key="0" class="animation-contents">
        <img :src="imgSrc" class="animation-image" />
      </div>
    </transition-group>

    <b-modal
      v-model="isShowingPlayBikkuraponModal"
      no-close-on-backdrop
      centered
      hide-header
      hide-footer
      ok-disabled
      cancel-disabled
    >
      <div class="content-subject">
        {{ $t('bikkuraPon.bikkuraPonConfirmation') }}
      </div>
      <div class="content-desc" :class="{ 'is-us': isSelectedUs }">
        <span>{{ $t('bikkuraPon.remainingTimes', { count: possibleCountToPlay }) }}</span>
      </div>
      <div>
        <b-row class="buttons">
          <b-col>
            <b-button block class="buttons-button-cancel" @click="setShowingPlayModal(false)">
              {{ $t('common.cancel') }}
            </b-button>
          </b-col>
          <b-col>
            <b-button block :disabled="isPlaying" class="buttons-button" @click="playBikkurapon()">
              {{ $t('bikkuraPon.start') }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <b-modal
      v-model="isShowingBikkuraponBusyModal"
      no-close-on-backdrop
      centered
      hide-header
      hide-footer
      ok-disabled
      cancel-disabled
    >
      <div class="content-subject">
        {{ $t('bikkuraPon.bikkuraPonInProgress') }}
      </div>
      <div class="content-desc">
        {{ $t('bikkuraPon.bikkuraPonInProgressDescription') }}
      </div>
      <div>
        <b-row class="buttons">
          <b-col>
            <b-button block class="buttons-button-cancel" @click="setShowingBusyModal(false)">
              {{ $t('common.close') }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <b-modal
      v-model="isShowingBikkuraponErrorModal"
      no-close-on-backdrop
      centered
      hide-header
      hide-footer
      ok-disabled
      cancel-disabled
    >
      <div class="content-subject">{{ $t('bikkuraPon.bikkuraPonUnabled') }}</div>
      <div class="content-desc">{{ $t('bikkuraPon.drawingFailure') }}</div>
      <div>
        <b-row class="buttons">
          <b-col>
            <b-button block class="buttons-button-cancel" @click="setShowingErrorModal(false)">
              {{ $t('common.close') }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import config from '@/config/config'
import * as api from '@/api'
import { fetchImages } from '@/api/fetchImages'
import sleep from '@/utils/sleep'

export default {
  props: {
    possibleCountToPlay: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      baseImages: ['01.jpg', '02.jpg', '03.jpg', '04.jpg'],
      imgSrc: '',
      isPlaying: false,
    }
  },
  computed: {
    ...mapGetters({
      shopId: 'seat/getShopId',
      seatNo: 'seat/getSeatNo',
      language: 'setting/getLanguage',
      isShowingPlayBikkuraponModal: 'modal/isShowingPlayBikkuraponModal',
      isShowingBikkuraponBusyModal: 'modal/isShowingBikkuraponBusyModal',
      isShowingBikkuraponErrorModal: 'modal/isShowingBikkuraponErrorModal',
      isShowingBikkuraponAnimationModal: 'modal/isShowingBikkuraponAnimationModal',
    }),
    imageDirectory() {
      return `${config.domain}/assets/image/side-menu-bikkurapon/pattern-0`
    },
    winImages() {
      const baseWinImages = this.baseImages.map((image) => `${this.imageDirectory}/win/${image}`)
      const lastImage = this.getLastImage('win', this.language)
      return [...baseWinImages, lastImage]
    },
    loseImages() {
      const baseLoseImages = this.baseImages.map((image) => `${this.imageDirectory}/lose/${image}`)
      const lastImage = this.getLastImage('lose', this.language)
      return [...baseLoseImages, lastImage]
    },
    isSelectedUs() {
      return this.language === 'us'
    },
  },
  mounted() {
    const images = this.winImages.concat(this.loseImages)
    return fetchImages(images)
  },
  methods: {
    ...mapActions({
      setShowingPlayModal: 'modal/setShowingPlayBikkuraponModal',
      setShowingBusyModal: 'modal/setShowingBikkuraponBusyModal',
      setShowingErrorModal: 'modal/setShowingBikkuraponErrorModal',
      setShowingAnimationModal: 'modal/setShowingBikkuraponAnimationModal',
    }),
    getLastImage(type, language) {
      switch (language) {
        case 'ja':
          return `${this.imageDirectory}/${type}/05.jpg`
        case 'zh-tw':
          return `${this.imageDirectory}/${type}/05_tw.jpg`
        case 'zh-cn':
          return `${this.imageDirectory}/${type}/05_cn.jpg`
        case 'us':
          return `${this.imageDirectory}/${type}/05_english.jpg`
        default:
          return `${this.imageDirectory}/${type}/05.jpg`
      }
    },
    async playBikkurapon() {
      if (this.isPlaying) return
      this.isPlaying = true

      // call Lottery
      try {
        const { status, data } = await api.drawBikkuraponLottery(this.shopId, this.seatNo)
        if (status === 202) {
          this.setShowingPlayModal(false)
          this.setShowingBusyModal(true)
          await sleep(1000) // wait until PlayModal is completely faded
          this.isPlaying = false
          return
        }
        this.setShowingPlayModal(false)
        // 1=落選, 2=当選
        if (data.result === 1) {
          await this.playAnimation('lose')
          this.isPlaying = false
        } else if (data.result === 2) {
          await this.playAnimation('win')
          api.playSideDeBikkuraponVideo(this.shopId, this.seatNo)
          this.isPlaying = false
        }
      } catch (e) {
        this.setShowingPlayModal(false)
        this.setShowingErrorModal(true)
        await sleep(2000) // wait until PlayModal is completely faded
        this.isPlaying = false
      }
    },
    async playAnimation(winOrLose) {
      this.setShowingAnimationModal(true) // fadeIn trigger
      await sleep(1000) // wait until fadeIn is over

      const images = winOrLose === 'win' ? this.winImages : this.loseImages
      // create array filled with index
      // e.g. [0, 1, 2, 3, 4, 5...]
      const arr = [...new Array(images.length).keys()]

      for (let i of arr) {
        this.imgSrc = images[i]
        await sleep(1000)
      }

      // 最後の画像のみ+1秒待つ
      await sleep(1000)

      // reset
      this.imgSrc = null
      this.setShowingAnimationModal(false) // fadeOut trigger
    },
  },
}
</script>

<style lang="scss" scoped>
.animation-contents {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background-color: #fff;
}
.animation-image {
  max-height: 100%;
}
.content {
  &-subject {
    font-size: 18px;
    font-weight: bold;
    color: #222222 !important;
  }
  &-desc {
    color: #222222 !important;
    padding: 20px 0;
    font-size: 14px;
  }
}
.buttons {
  padding: 10px 0;
  margin: 10px 0 0;
  &-button {
    font-size: 18px;
    font-weight: bold;
    background-color: #d21b24;
    border-color: #d21b24;
    padding: 10px 0px;
    &-cancel {
      font-size: 18px;
      font-weight: bold;
      padding: 10px 0px;
      background-color: #656565;
    }
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.content-desc {
  white-space: pre-wrap;

  &.is-us {
    color: #d21b24 !important;
  }
}
</style>
